/**
 * RouteHead — keeps <link rel="canonical"> and <meta property="og:url">
 * in sync with the current route. Mounted once inside BrowserRouter.
 *
 * Why client-side too?
 *  • The prerender worker captures whatever the DOM emits when Googlebot fetches.
 *    For SPA routes the shipped index.html canonical/og:url were hardcoded — every
 *    crawl would record `https://isupplyai.com` regardless of the actual URL.
 *  • For real users navigating in-app, this also keeps share buttons & analytics
 *    pointing at the right URL.
 *
 * Per-page <title> and <meta name="description"> are owned by individual page
 * components (e.g., PageSEO, BlogPost, GlossaryTermPage, ComparisonPage).
 * RouteHead ONLY handles canonical + og:url because those are URL-derived.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ensureTag = (selector, create) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const RouteHead = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      const url = window.location.origin + window.location.pathname;

      // 1. Canonical link — create if missing (index.html doesn't ship one)
      const canonical = ensureTag('link[rel="canonical"]', () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'canonical');
        return l;
      });
      canonical.setAttribute('href', url);

      // 2. og:url — create if missing
      const ogUrl = ensureTag('meta[property="og:url"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:url');
        return m;
      });
      ogUrl.setAttribute('content', url);
    } catch {
      /* defensive — never crash the app over SEO */
    }
  }, [location.pathname]);

  return null;
};

export default RouteHead;
